// Browse-all-verbs view — the foundation-slice vertical proof.
//
// Flow demonstrated end-to-end:
//   public verbs (guest-readable) → overlay user_workspace (pins/notes) →
//   render → click a verb → conjugation detail (modal) + notes/pin (guest CTA).
import { fetchVerbs, fetchWorkspace, overlay } from "../lib/data.js";
import {
  conjugate,
  conjugateSubj,
  imperative,
  diffMarkHTML,
  participles,
  PRONOUNS,
  TENSES,
  TENSE_LABELS,
  SUBJUNCTIVE_TENSES,
  SUBJUNCTIVE_LABELS,
  IMPERATIVE_ROWS,
  IMPERATIVE_LABELS,
} from "../data/engine.js";
import { renderNotesPin } from "../ui/notesPin.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { escapeHtml, highlightMatch, groupClass } from "../ui/helpers.js";
import { speakButtonHTML } from "../lib/audio.js";
import { navigate, navigateBack } from "../router/router.js";

let cache = null; // { verbs: overlaid[] }

async function loadData() {
  const [verbs, workspace] = await Promise.all([fetchVerbs(), fetchWorkspace()]);
  cache = { verbs: overlay(verbs, workspace, "verbs") };
  return cache;
}

export async function searchView(app, params = {}) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading verbs…</div>`;
  const data = await loadData();
  const total = data.verbs.length;

  app.innerHTML = `
    <section>
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800">Browse All Verbs</h2>
        <p class="text-sage-600 text-sm mt-1">Search across <b>${total}</b> Italian verbs — type in English <span class="text-sage-400">or</span> Italian.</p>
      </div>
      <div class="relative mb-3">
        <div class="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 pointer-events-none">🔍</div>
        <input id="verb-search" type="text" placeholder="Try 'to run' or 'correre'…"
          class="w-full pl-11 pr-4 py-3 rounded-xl border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-base" />
      </div>
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <span class="text-xs text-sage-500 uppercase tracking-wide mr-1">Search in:</span>
        <button class="dir-btn px-3 py-1 rounded-full text-xs font-medium" data-dir="both">Both</button>
        <button class="dir-btn px-3 py-1 rounded-full text-xs font-medium" data-dir="es">Italian</button>
        <button class="dir-btn px-3 py-1 rounded-full text-xs font-medium" data-dir="en">English</button>
        <span class="text-xs text-sage-300 mx-2">·</span>
        <select id="filter-group" class="border border-sage-200 rounded-lg px-3 py-1.5 text-xs bg-white">
          <option value="">All groups</option>
          <option value="regular">Regular</option>
          <option value="irregular">Irregular</option>
          <option value="stem-change">Stem-changing</option>
        </select>
      </div>
      <p id="result-count" class="text-xs text-sage-500 mb-3"></p>
      <div id="verb-list" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2"></div>
    </section>`;

  const input = app.querySelector("#verb-search");
  const filterSel = app.querySelector("#filter-group");
  const list = app.querySelector("#verb-list");
  const count = app.querySelector("#result-count");
  let direction = "both";

  function setDirection(d) {
    direction = d;
    app.querySelectorAll(".dir-btn").forEach((b) => {
      const on = b.dataset.dir === d;
      b.className = `dir-btn px-3 py-1 rounded-full text-xs font-medium border ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200"
      }`;
    });
    render();
  }

  function render() {
    const q = input.value.trim().toLowerCase();
    const g = filterSel.value;
    let results = data.verbs.filter((v) => {
      if (g && v.pattern_group !== g) return false;
      if (!q) return true;
      const inEs = v.infinitive.toLowerCase().includes(q);
      const inEn = (v.english_translation || "").toLowerCase().includes(q);
      if (direction === "es") return inEs;
      if (direction === "en") return inEn;
      return inEs || inEn;
    });
    if (q) {
      results = results.slice().sort((a, b) => {
        const aE = a.infinitive.toLowerCase() === q || (a.english_translation || "").toLowerCase() === q ? 0 : 1;
        const bE = b.infinitive.toLowerCase() === q || (b.english_translation || "").toLowerCase() === q ? 0 : 1;
        if (aE !== bE) return aE - bE;
        return (a.rank || 9999) - (b.rank || 9999);
      });
    }
    count.innerHTML = q
      ? `<b>${results.length}</b> match${results.length === 1 ? "" : "es"} for "<span class='text-sage-700'>${escapeHtml(q)}</span>"`
      : `${results.length} verbs total`;
    list.innerHTML = "";
    results.slice(0, 200).forEach((v) => {
      const item = document.createElement("button");
      item.className =
        "bg-white border border-sage-100 hover:border-sage-300 rounded-lg p-3 text-left transition flex items-center justify-between gap-2";
      item.innerHTML = `
        <div class="min-w-0">
          <p class="font-semibold text-sage-800 truncate">${v.emoji ? v.emoji + " " : ""}${highlightMatch(v.infinitive, q)}${v.is_pinned ? ' <span title="Pinned">📌</span>' : ""}</p>
          <p class="text-xs text-sage-500 truncate">${highlightMatch(v.english_translation || "", q)}</p>
        </div>
        <span class="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${groupClass(v.pattern_group)}">${v.pattern_group}</span>`;
      item.addEventListener("click", () => navigate("verb-detail", { inf: v.infinitive }));
      list.appendChild(item);
    });
    if (!results.length && q) {
      list.innerHTML = `<div class="col-span-full text-center py-8 text-sage-500">
        <p class="text-2xl mb-2">🤔</p><p>No verbs found for "${escapeHtml(q)}"</p>
        <p class="text-xs mt-2 text-sage-400">Try a different spelling, drop accents, or change the search direction.</p></div>`;
    }
    if (results.length > 200) {
      const more = document.createElement("p");
      more.className = "col-span-full text-xs text-sage-400 text-center py-2";
      more.textContent = "Showing first 200. Keep typing to narrow.";
      list.appendChild(more);
    }
  }

  input.addEventListener("input", render);
  filterSel.addEventListener("change", render);
  app.querySelectorAll(".dir-btn").forEach((b) =>
    b.addEventListener("click", () => setDirection(b.dataset.dir))
  );
  setDirection("both");

}

// ---- Conjugation modal -------------------------------------------------
// Renders one cell of a conjugation table. Irregular forms are letter-diffed
// against the regular baseline so only the changed characters render red.
function formHTML(form, regular, irregular) {
  return irregular ? diffMarkHTML(form ?? "", regular ?? "") : escapeHtml(form ?? "");
}

// Build the five indicative tense tables. `table-fixed` + a fixed-width
// pronoun column makes every block share the same column geometry, so the
// top and bottom tenses line up exactly.
function tenseTablesHTML(infinitive) {
  return TENSES.map((t) => {
    const { forms, regular } = conjugate(infinitive, t);
    const rows = PRONOUNS.map((p, i) => {
      const irregular = forms[i] !== regular[i];
      return `
        <tr class="border-b border-sage-50 last:border-0">
          <td class="py-1 pr-3 text-sage-400 text-xs whitespace-nowrap align-top w-20">${p}</td>
          <td class="py-1 font-medium text-sage-800">${formHTML(forms[i], regular[i], irregular)}</td>
        </tr>`;
    }).join("");
    return `
      <div class="min-w-0">
        <p class="text-xs font-semibold text-sage-500 uppercase tracking-wide mb-1">${TENSE_LABELS[t] || t}</p>
        <table class="w-full table-fixed text-sm"><tbody>${rows}</tbody></table>
      </div>`;
  }).join("");
}

// The three subjunctive tenses — same table geometry as the indicative blocks.
function subjTablesHTML(infinitive) {
  return SUBJUNCTIVE_TENSES.map((t) => {
    const { forms, regular } = conjugateSubj(infinitive, t);
    const rows = PRONOUNS.map((p, i) => {
      const irregular = forms[i] !== regular[i];
      return `
        <tr class="border-b border-sage-50 last:border-0">
          <td class="py-1 pr-3 text-sage-400 text-xs whitespace-nowrap align-top w-20">${p}</td>
          <td class="py-1 font-medium text-sage-800">${formHTML(forms[i], regular[i], irregular)}</td>
        </tr>`;
    }).join("");
    return `
      <div class="min-w-0">
        <p class="text-xs font-semibold text-sage-500 uppercase tracking-wide mb-1">${SUBJUNCTIVE_LABELS[t] || t}</p>
        <table class="w-full table-fixed text-sm"><tbody>${rows}</tbody></table>
      </div>`;
  }).join("");
}

// Imperative — one table, affirmative + negative columns per person.
function imperativeTableHTML(infinitive) {
  const imp = imperative(infinitive);
  const rows = IMPERATIVE_ROWS.map((r) => {
    const cell = imp[r] || {};
    const affIrr = cell.aff !== cell.affReg;
    const negIrr = cell.neg !== cell.negReg;
    return `
      <tr class="border-b border-sage-50 last:border-0">
        <td class="py-1 pr-3 text-sage-400 text-xs whitespace-nowrap align-top w-20">${IMPERATIVE_LABELS[r] || r}</td>
        <td class="py-1 pr-3 font-medium text-sage-800">${formHTML(cell.aff, cell.affReg, affIrr)}</td>
        <td class="py-1 font-medium text-sage-600">${formHTML(cell.neg, cell.negReg, negIrr)}</td>
      </tr>`;
  }).join("");
  return `
    <table class="w-full text-sm">
      <thead><tr>
        <th></th>
        <th class="text-left text-[10px] uppercase tracking-wide text-sage-500 pb-1">Affirmative</th>
        <th class="text-left text-[10px] uppercase tracking-wide text-sage-500 pb-1">Negative</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

// A remembered Show/Hide toggle for a mood section (persists per browser).
function setupMoodToggle(card, btnSel, sectionSel, key, label) {
  const btn = card.querySelector(btnSel);
  const section = card.querySelector(sectionSel);
  if (!btn || !section) return;
  let on = false;
  try { on = localStorage.getItem(key) === "1"; } catch { /* private mode */ }
  const apply = () => {
    section.classList.toggle("hidden", !on);
    btn.textContent = (on ? "Hide " : "Show ") + label;
    btn.classList.toggle("bg-sage-50", on);
    btn.classList.toggle("border-sage-300", on);
  };
  apply();
  btn.addEventListener("click", () => {
    on = !on;
    try { localStorage.setItem(key, on ? "1" : "0"); } catch { /* private mode */ }
    apply();
  });
}

function participlesHTML(infinitive) {
  const p = participles(infinitive);
  const ger = p.gerundio !== p.gerundioReg ? diffMarkHTML(p.gerundio, p.gerundioReg) : escapeHtml(p.gerundio);
  const par = p.participio !== p.participioReg ? diffMarkHTML(p.participio, p.participioReg) : escapeHtml(p.participio);
  return `
    <div class="flex flex-wrap gap-x-8 gap-y-1 mt-3 pt-3 border-t border-sage-100">
      <div>
        <span class="text-[10px] uppercase tracking-wide text-sage-500 block">Gerundio · Present participle</span>
        <span class="font-medium text-sage-800 text-sm">${ger}</span>
      </div>
      <div>
        <span class="text-[10px] uppercase tracking-wide text-sage-500 block">Participio · Past participle</span>
        <span class="font-medium text-sage-800 text-sm">${par}</span>
      </div>
    </div>`;
}

// Full-page verb detail (replaces the old hovering modal — long content with
// subjunctive + imperative scrolls naturally and each verb gets its own URL).
export async function verbDetailView(app, params = {}) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading…</div>`;
  const data = cache || (await loadData());
  const v = data.verbs.find((x) => x.infinitive === params.inf);

  if (!v) {
    app.innerHTML = `
      <div class="max-w-2xl mx-auto py-16 text-center">
        <p class="text-3xl mb-3">🤔</p>
        <p class="text-sage-700 font-medium">That verb isn't here.</p>
        <button data-back class="mt-4 text-sm font-medium text-sage-600 hover:text-sage-800">← Back to verbs</button>
      </div>`;
    app.querySelector("[data-back]").addEventListener("click", () => navigate("search"));
    return;
  }

  app.innerHTML = `
    <article class="max-w-3xl mx-auto fade-in">
      <button data-back class="text-sm font-medium text-sage-600 hover:text-sage-800 mb-4 inline-flex items-center gap-1">← All verbs</button>
      <div class="bg-white border border-sage-100 rounded-2xl p-5 sm:p-6">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h1 class="text-2xl font-bold text-sage-800 flex items-center gap-1.5">${v.emoji ? v.emoji + " " : ""}${escapeHtml(v.infinitive)}${speakButtonHTML(v.infinitive, { size: "lg" })}</h1>
            <p class="text-sage-500 text-sm">${escapeHtml(v.english_translation || "")}</p>
            ${v.note ? `<p class="text-xs text-sage-400 mt-1">${escapeHtml(v.note)}</p>` : ""}
          </div>
          <span class="text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap ${groupClass(v.pattern_group)}">${v.pattern_group}</span>
        </div>
        ${participlesHTML(v.infinitive)}
        <div class="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 mt-4" data-tenses></div>
        <div class="mt-5 flex flex-wrap gap-2">
          <button data-toggle-subj class="text-xs font-medium px-3 py-1.5 rounded-full border border-sage-200 text-sage-600 hover:border-sage-300 transition">Show Subjunctive</button>
          <button data-toggle-imp class="text-xs font-medium px-3 py-1.5 rounded-full border border-sage-200 text-sage-600 hover:border-sage-300 transition">Show Imperative</button>
        </div>
        <div class="hidden mt-4 pt-4 border-t border-sage-100" data-subj-section>
          <p class="text-xs font-bold text-sage-700 uppercase tracking-wide mb-2">Congiuntivo · Subjunctive</p>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4" data-subj></div>
        </div>
        <div class="hidden mt-4 pt-4 border-t border-sage-100" data-imp-section>
          <p class="text-xs font-bold text-sage-700 uppercase tracking-wide mb-2">Imperativo · Imperative</p>
          <div class="max-w-md" data-imperative></div>
        </div>
        <div data-notes class="mt-2"></div>
      </div>
    </article>`;

  app.querySelector("[data-tenses]").innerHTML = tenseTablesHTML(v.infinitive);
  app.querySelector("[data-subj]").innerHTML = subjTablesHTML(v.infinitive);
  app.querySelector("[data-imperative]").innerHTML = imperativeTableHTML(v.infinitive);
  setupMoodToggle(app, "[data-toggle-subj]", "[data-subj-section]", "azzurro_show_subj", "Subjunctive");
  setupMoodToggle(app, "[data-toggle-imp]", "[data-imp-section]", "azzurro_show_imp", "Imperative");
  app.querySelector("[data-notes]").appendChild(renderNotesPin(v, "verbs", () => openAuthPanel("signin")));
  app.querySelector("[data-back]").addEventListener("click", () => navigateBack("search"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}
